import React, { useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Popover,
  Typography,
  TextField,
  Stack,
  CircularProgress,
} from '@mui/material';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import SyncIcon from '@mui/icons-material/Sync';
import DownloadIcon from '@mui/icons-material/Download';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import RefreshIcon from '@mui/icons-material/Refresh';

const IntegrationActions = ({
  status,
  onDisconnect,
  onReconnect,
  onSyncInventory,
  onImportSales,
  onPullInventory,
  isDisconnecting = false,
  isSyncing = false,
  isImporting = false,
  isPulling = false,
}) => {
  const [disconnectAnchorEl, setDisconnectAnchorEl] = useState(null);
  const [importAnchorEl, setImportAnchorEl] = useState(null);
  const [importStartDate, setImportStartDate] = useState('');
  const [importEndDate, setImportEndDate] = useState('');

  if (!status || !status.status) return null;

  const isConnected = status.status === 'connected';
  const needsReconnect = status.status === 'token_expired' || status.status === 'error';

  const handleDisconnectClick = (e) => {
    setDisconnectAnchorEl(e.currentTarget);
  };

  const handleDisconnectConfirm = () => {
    setDisconnectAnchorEl(null);
    onDisconnect();
  };

  const handleImportClick = (e) => {
    setImportAnchorEl(e.currentTarget);
    setImportStartDate('');
    setImportEndDate('');
  };

  const handleImportConfirm = () => {
    const dateRange = {};
    if (importStartDate) dateRange.startDate = importStartDate;
    if (importEndDate) dateRange.endDate = importEndDate;
    setImportAnchorEl(null);
    onImportSales(dateRange);
  };

  if (needsReconnect) {
    return (
      <Tooltip title="Reconnect to platform">
        <Button
          size="small"
          variant="contained"
          color="warning"
          startIcon={<RefreshIcon />}
          onClick={onReconnect}
          sx={{ textTransform: 'none' }}
          aria-label="Reconnect to platform"
        >
          Reconnect
        </Button>
      </Tooltip>
    );
  }

  if (!isConnected) return null;

  return (
    <>
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <Tooltip title="Sync inventory with platform">
          <IconButton
            size="small"
            color="primary"
            onClick={() => onSyncInventory()}
            disabled={isSyncing}
            aria-label="Sync inventory"
          >
            {isSyncing ? <CircularProgress size={18} /> : <SyncIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Pull inventory from platform">
          <IconButton
            size="small"
            color="primary"
            onClick={onPullInventory}
            disabled={isPulling}
            aria-label="Pull inventory from platform"
          >
            {isPulling ? <CircularProgress size={18} /> : <CloudDownloadIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Import sales from platform">
          <IconButton
            size="small"
            color="primary"
            onClick={handleImportClick}
            disabled={isImporting}
            aria-label="Import sales"
          >
            {isImporting ? <CircularProgress size={18} /> : <DownloadIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Disconnect from platform">
          <IconButton
            size="small"
            color="error"
            onClick={handleDisconnectClick}
            disabled={isDisconnecting}
            aria-label="Disconnect from platform"
          >
            {isDisconnecting ? <CircularProgress size={18} /> : <LinkOffIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Disconnect Confirmation Popover */}
      <Popover
        open={Boolean(disconnectAnchorEl)}
        anchorEl={disconnectAnchorEl}
        onClose={() => setDisconnectAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Box sx={{ p: 2, maxWidth: 300 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Disconnecting will stop all syncing with this platform. Are you sure?
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              onClick={() => setDisconnectAnchorEl(null)}
              variant="outlined"
              size="small"
              sx={{ textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDisconnectConfirm}
              variant="contained"
              color="error"
              size="small"
              disabled={isDisconnecting}
              sx={{ textTransform: 'none' }}
            >
              {isDisconnecting ? <CircularProgress size={18} color="inherit" /> : 'Disconnect'}
            </Button>
          </Box>
        </Box>
      </Popover>

      {/* Import Sales Date Range Popover */}
      <Popover
        open={Boolean(importAnchorEl)}
        anchorEl={importAnchorEl}
        onClose={() => setImportAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, minWidth: 280 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
            Import Sales
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="Start Date"
              type="date"
              value={importStartDate}
              onChange={(e) => setImportStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              fullWidth
            />
            <TextField
              label="End Date"
              type="date"
              value={importEndDate}
              onChange={(e) => setImportEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              fullWidth
            />
            <Typography variant="caption" color="text.secondary">
              Leave dates empty to import all recent sales.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                onClick={() => setImportAnchorEl(null)}
                variant="outlined"
                size="small"
                sx={{ textTransform: 'none' }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleImportConfirm}
                variant="contained"
                size="small"
                disabled={isImporting}
                startIcon={isImporting ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />}
                sx={{ textTransform: 'none' }}
              >
                Import
              </Button>
            </Box>
          </Stack>
        </Box>
      </Popover>
    </>
  );
};

export default IntegrationActions;
